import api from "../utils/api";

const ADMIN_COMPLIANCE_BASE = "/admin/compliance";

export interface IComplianceExtractionEntry {
  _id: string;
  entryDate: string;
  entryDayKey: string;
  extractedGallons: number;
  notes?: string;
  source?: "manual" | "imported";
}

export interface ITruckProfile {
  _id: string;
  truckNumber: string;
  vehicleOwner: string;
  truckCapacityGallons: number;
  active: boolean;
  notes?: string;
}

export interface IComplianceTripLog {
  _id: string;
  tripDate: string;
  tripType: "delivery";
  generalDestination?: string;
  truckNumber?: string;
  vehicleOwner?: string;
  truckCapacityGallons?: number;
  gallonsDelivered?: number;
  reviewStatus: "ready" | "needs_review";
  reviewNotes: string[];
  serviceLabel?: string;
}

export interface IComplianceReportRun {
  _id: string;
  reportType: "daily" | "monthly" | "annual";
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  dueDate?: string;
  status: "generated" | "warning";
  generatedByType: "automation" | "admin";
  warningMessages: string[];
  totals: {
    extractionGallons: number;
    tripCount: number;
    deliveredGallons: number;
    needsReviewCount: number;
  };
  createdAt: string;
}

// Browser download helper used by the report history table for CSV/PDF artifacts.
const downloadBlob = (blob: Blob, filename: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(objectUrl);
};

// The admin page talks to the dedicated compliance API so it does not depend on order screens.
export const ComplianceModel = {
  async getExtractionEntries(startDate?: string, endDate?: string) {
    const response = await api.get<IComplianceExtractionEntry[]>(
      `${ADMIN_COMPLIANCE_BASE}/extraction-entries`,
      { params: { startDate, endDate } }
    );
    return response.data;
  },

  async saveExtractionEntry(payload: {
    entryDate: string;
    extractedGallons: number;
    notes?: string;
  }) {
    const response = await api.post<IComplianceExtractionEntry>(
      `${ADMIN_COMPLIANCE_BASE}/extraction-entries`,
      payload
    );
    return response.data;
  },

  async getTruckProfiles() {
    const response = await api.get<ITruckProfile[]>(
      `${ADMIN_COMPLIANCE_BASE}/truck-profiles`
    );
    return response.data;
  },

  async createTruckProfile(payload: {
    truckNumber: string;
    vehicleOwner: string;
    truckCapacityGallons: number;
    notes?: string;
  }) {
    const response = await api.post<ITruckProfile>(
      `${ADMIN_COMPLIANCE_BASE}/truck-profiles`,
      payload
    );
    return response.data;
  },

  async updateTruckProfile(
    truckProfileId: string,
    payload: Partial<ITruckProfile>
  ) {
    const response = await api.put<ITruckProfile>(
      `${ADMIN_COMPLIANCE_BASE}/truck-profiles/${truckProfileId}`,
      payload
    );
    return response.data;
  },

  async deleteTruckProfile(truckProfileId: string) {
    await api.delete(`${ADMIN_COMPLIANCE_BASE}/truck-profiles/${truckProfileId}`);
  },

  async getTripLogs(startDate?: string, endDate?: string) {
    const response = await api.get<IComplianceTripLog[]>(
      `${ADMIN_COMPLIANCE_BASE}/trip-logs`,
      { params: { startDate, endDate } }
    );
    return response.data;
  },

  async syncTripLogs(startDate: string, endDate: string) {
    // Sync does not generate a final report; it materializes reviewable trip rows for the chosen window.
    const response = await api.post<{ syncedCount: number }>(
      `${ADMIN_COMPLIANCE_BASE}/trip-logs/sync`,
      { startDate, endDate }
    );
    return response.data;
  },

  async updateTripLog(
    tripLogId: string,
    payload: {
      generalDestination?: string;
      vehicleOwner?: string;
      truckCapacityGallons?: number;
      gallonsDelivered?: number;
    }
  ) {
    const response = await api.put<IComplianceTripLog>(
      `${ADMIN_COMPLIANCE_BASE}/trip-logs/${tripLogId}`,
      payload
    );
    return response.data;
  },

  async getReportRuns() {
    const response = await api.get<IComplianceReportRun[]>(
      `${ADMIN_COMPLIANCE_BASE}/reports`
    );
    return response.data;
  },

  async generateReport(payload: {
    reportType: "daily" | "monthly" | "annual";
    referenceDate: string;
  }) {
    // Report type + reference date lets the backend derive the exact period to export.
    const response = await api.post<IComplianceReportRun>(
      `${ADMIN_COMPLIANCE_BASE}/reports/generate`,
      payload
    );
    return response.data;
  },

  async downloadReport(reportRunId: string, format: "csv" | "pdf") {
    const response = await api.get<Blob>(
      `${ADMIN_COMPLIANCE_BASE}/reports/${reportRunId}/${format}`,
      { responseType: "blob" as any }
    );
    downloadBlob(
      response.data as unknown as Blob,
      `compliance-report-${reportRunId}.${format}`
    );
  },
};
